import React, { useState, useEffect } from "react";
import { Navigation, ScanFace, CheckCircle, Clock, LogOut, Cake, Award, Megaphone, Quote as QuoteIcon, Camera, Calendar } from "lucide-react";
import api, { BASE } from "../../api";
import { uploadUrl } from "../../uploadUrl";
import { K } from "../../theme";
import { Btn, Card, Pill } from "../components";
import { CelebrationModal } from "../../components/CelebrationModal";
import { fmtHM, fmtTime, fmtMonthDay, fmtAnnouncementDate } from "../utils";

// Today → the same date next month, matching the desktop dashboard.
const MONTH_AHEAD_DAYS = (() => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return Math.round((next - now) / 86400000);
})();

const CELEBRATION_WINDOW_LABEL = (() => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const f = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return `${f(now)} – ${f(end)}`;
})();

export const Home = ({ me, todayRec, onGoAttendance }) => {
  const greet = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })();
  const hasCheckIn = todayRec && todayRec.check_in;
  const hasCheckOut = todayRec && todayRec.check_out;
  const [announcements, setAnnouncements] = useState([]);
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [] });
  const [selectedCelebration, setSelectedCelebration] = useState(null);
  const [qotd, setQotd] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const myEmpId = me?.user?.employee_id;

  useEffect(() => {
    api.get('/api/announcements').then(d => setAnnouncements(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
    api.get(`/api/employees/celebrations?days=${MONTH_AHEAD_DAYS}`).then(d => setCelebrations({ birthdays: d?.birthdays || [], anniversaries: d?.anniversaries || [] })).catch(() => {});
    api.get('/api/quotes/today').then(d => setQotd(d?.quote || null)).catch(() => setQotd(null));
    api.get('/api/events/recent').then(d => setRecentEvents(Array.isArray(d?.rows) ? d.rows : [])).catch(() => setRecentEvents([]));
    // Scoped server-side to the employee's own office (admin/hr_head/centre_head see every office).
    api.get('/api/holidays/upcoming?limit=5').then(d => setHolidays(Array.isArray(d) ? d : [])).catch(() => setHolidays([]));
  }, []);

  const todayMD = (() => { const d = new Date(); return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const isToday = (md) => md === todayMD;
  const combined = [
    ...celebrations.birthdays.map(b => ({ ...b, kind: 'birthday' })),
    ...celebrations.anniversaries.map(a => ({ ...a, kind: 'anniversary' })),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || '')).slice(0, 12);

  return <div className="px-4 pt-4 pb-[100px]">
    <div className="mb-[18px]">
      <div className="text-[15px] text-t3">{greet},</div>
      <div className="text-[22px] font-extrabold text-t0">{me?.employee?.name || "—"}</div>
      <div className="text-[14px] text-t2 mt-[2px]">{myEmpId} · {me?.employee?.role_title || "—"}</div>
    </div>

    <Card
      className="mb-[14px]"
      style={{ background: hasCheckOut ? K.gb : hasCheckIn ? K.blb : K.s, borderColor: hasCheckOut ? K.gd : hasCheckIn ? `${K.bl}30` : K.b0 }}
    >
      <div className={`flex items-center gap-[10px] ${hasCheckIn ? "mb-[10px]" : ""}`}>
        <div className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center" style={{ background: hasCheckOut ? K.g : hasCheckIn ? K.bl : K.t3 }}>
          {hasCheckOut ? <CheckCircle size={22} color="#fff" /> : hasCheckIn ? <Clock size={22} color="#fff" /> : <ScanFace size={22} color="#fff" />}
        </div>
        <div className="flex-1">
          <div className="text-[15px] text-t3">Today</div>
          <div className="text-[17px] font-bold text-t0">{hasCheckOut ? "Done for today" : hasCheckIn ? "Currently working" : "Not checked in"}</div>
        </div>
      </div>
      {hasCheckIn && <div className="flex justify-around pt-[10px] pb-1 border-t border-b0">
        <div className="text-center"><div className="text-[14px] text-t3">IN</div><div className="text-[15px] font-bold text-g">{fmtTime(todayRec.check_in)}</div></div>
        <div className="text-center"><div className="text-[14px] text-t3">OUT</div><div className="text-[15px] font-bold" style={{ color: hasCheckOut ? K.bl : K.t3 }}>{hasCheckOut ? fmtTime(todayRec.check_out) : "—"}</div></div>
        <div className="text-center"><div className="text-[14px] text-t3">HOURS</div><div className="text-[15px] font-bold text-t0">{fmtHM(todayRec.work_hours)}</div></div>
      </div>}
    </Card>

    <Btn full icon={hasCheckIn && !hasCheckOut ? LogOut : Navigation} onClick={onGoAttendance} style={{ marginBottom: 18, background: hasCheckIn && !hasCheckOut ? K.bl : K.p }}>
      {hasCheckOut ? "View Attendance" : hasCheckIn ? "Check Out" : "Check In Now"}
    </Btn>

    {qotd && (
      <Card className="mb-[14px]" style={{ background: `linear-gradient(135deg, ${K.p}12, ${K.pu}12)`, borderColor: `${K.p}30` }}>
        <div className="flex items-start gap-[10px]">
          <QuoteIcon size={18} color={K.p} className="flex-shrink-0 mt-[2px]" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold uppercase tracking-[0.15em] text-t3 mb-[4px]">Quote of the Day</div>
            <div className="text-[15px] italic font-medium text-t0 leading-[1.5]">&ldquo;{qotd.text}&rdquo;</div>
            {qotd.author && <div className="text-[14px] text-t2 mt-[6px] not-italic">— {qotd.author}</div>}
          </div>
        </div>
      </Card>
    )}

    {recentEvents.length > 0 && (
      <Card className="mb-[14px]">
        <div className="flex items-center gap-2 mb-[10px]">
          <Camera size={16} color={K.p} />
          <div className="text-[15px] font-bold text-t0">Recent Events</div>
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-[2px] px-[2px] pb-1">
          {recentEvents.map(ev => (
            <div key={ev.id} className="flex-shrink-0 w-[130px] rounded-lg overflow-hidden border border-b0 bg-bg2">
              <div className="aspect-[4/3] relative">
                {ev.cover_photo
                  ? <img src={BASE + '/' + ev.cover_photo} alt={ev.title} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center"><Camera size={20} className="text-t3 opacity-40" /></div>
                }
                {ev.photo_count > 0 && (
                  <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[12px] px-[5px] py-[1px] rounded-full">{ev.photo_count}</div>
                )}
              </div>
              <div className="p-[6px]">
                <div className="text-[14px] font-semibold text-t0 truncate">{ev.title}</div>
                <div className="text-[12px] text-t3 truncate">{ev.office_city || ev.office_name || ev.company_name || '—'}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )}

    <Card className="mb-[14px]">
      <div className="flex items-center gap-2 mb-[10px]">
        <Megaphone size={16} color={K.p} />
        <div className="text-[15px] font-bold text-t0">Announcements</div>
      </div>
      {announcements.length === 0
        ? <div className="text-[14px] text-t3 text-center py-[10px]">No announcements yet</div>
        : <div>{announcements.slice(0, 4).map((a, idx) => <div key={a.id} className={`py-[10px] ${idx > 0 ? "border-t border-b0" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-t0 leading-[1.4]">{a.is_pinned && <span className="text-a mr-[5px]">📌</span>}{a.title}</div>
              <div className="text-[14px] text-t3 mt-[3px]">
                {a.author && <span>{a.author}</span>}
                {a.date && <span> · {fmtAnnouncementDate(a.date)}</span>}
              </div>
            </div>
            {a.type && <Pill c={K.bl}>{a.type}</Pill>}
          </div>
        </div>)}</div>}
    </Card>

    <Card className="mb-[14px]">
      <div className="flex items-center gap-2 mb-[10px]">
        <Cake size={16} color={K.p} />
        <div className="text-[15px] font-bold text-t0">Celebrations</div>
        <span className="text-[14px] text-t3 ml-auto">{CELEBRATION_WINDOW_LABEL}</span>
      </div>
      {combined.length === 0
        ? <div className="text-[14px] text-t3 text-center py-[10px]">No upcoming birthdays or work anniversaries</div>
        : <div>{combined.map((c, i) => {
          const itsToday = isToday(c.date);
          const Ic = c.kind === 'birthday' ? Cake : Award;
          const accent = c.kind === 'birthday' ? K.pu : K.bl;
          return <div key={c.kind + c.employee_id}
                      onClick={() => setSelectedCelebration({ ...c, type: c.kind })}
                      className={`flex items-center gap-[10px] py-[10px] cursor-pointer active:opacity-75 ${i > 0 ? "border-t border-b0" : ""}`}>
            {c.photo
              ? <img src={uploadUrl(c.photo)} alt={c.name} className="w-9 h-9 rounded-full object-cover border-2" style={{ borderColor: `${accent}30` }} />
              : <div className="w-9 h-9 rounded-full flex items-center justify-center text-[15px] font-bold text-white" style={{ background: `linear-gradient(145deg,${accent},${accent}88)` }}>{(c.name || '?').split(' ').map(s => s[0]).join('').slice(0, 2)}</div>}
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-t0 flex items-center gap-[6px]">
                <Ic size={12} color={accent} />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">{c.name}</span>
                {c.employee_id === myEmpId && <Pill c={K.g}>You</Pill>}
              </div>
              <div className="text-[14px] text-t3 mt-[2px]">
                {c.kind === 'birthday'
                  ? (itsToday ? "Birthday today 🎉" : `Birthday on ${fmtMonthDay(c.date)}`)
                  : (itsToday ? `${c.years}-year work anniversary today 🎉` : `${c.years}-year anniversary on ${fmtMonthDay(c.date)}`)}
              </div>
            </div>
          </div>;
        })}</div>}
    </Card>

    <Card className="mb-[14px]">
      <div className="flex items-center gap-2 mb-[10px]">
        <Calendar size={16} color={K.p} />
        <div className="text-[15px] font-bold text-t0">Upcoming Holidays</div>
      </div>
      {holidays.length === 0
        ? <div className="text-[14px] text-t3 text-center py-[10px]">No upcoming holidays</div>
        : <div>{holidays.map((h, i) => {
          const d = h.date ? new Date(h.date + "T12:00:00") : null;
          const daysAway = d ? Math.floor((d - new Date(new Date().toDateString())) / 86400000) : null;
          const away = daysAway === 0 ? "Today" : daysAway === 1 ? "Tomorrow" : daysAway != null ? `in ${daysAway}d` : "";
          return <div key={h.id || i} className={`flex items-center gap-[10px] py-[10px] ${i > 0 ? "border-t border-b0" : ""}`}>
            <div className="w-9 h-9 rounded-lg flex flex-col items-center justify-center flex-shrink-0" style={{ background: K.ab }}>
              <div className="text-[14px] font-extrabold leading-none text-t0">{d ? d.getDate() : "—"}</div>
              <div className="text-[12px] font-semibold uppercase text-t3">{d ? d.toLocaleDateString("en-IN", { month: "short" }) : ""}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-t0 truncate">{h.name}</div>
              <div className="text-[14px] text-t3 mt-[2px] truncate">{h.office_name || ""}</div>
            </div>
            <Pill c={K.a}>{away}</Pill>
          </div>;
        })}</div>}
    </Card>

    {selectedCelebration && (
      <CelebrationModal
        item={selectedCelebration}
        onClose={() => setSelectedCelebration(null)}
      />
    )}
  </div>;
};
